import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { heroLoad } from '@/lib/motion'
import { lockScroll, unlockScroll } from '@/lib/lenis-store'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import {
    navigation,
    contact,
    social,
    siteConfig,
    author,
    availability
} from '@/constants/site'

/**
 * "Edinburgh" out of "Edinburgh, Scotland", for the one-line bar descriptor.
 * Resolved once at module scope rather than split on every render, since the
 * constant behind it never changes.
 */
const CITY = contact.city.split(',')[0]

const previews: Record<string, string> = {
    '/': '/images/cap-hero.jpg',
    '/portfolio': '/images/cap-work-chair.jpg',
    '/blog': '/images/cap-blog-1.jpg',
    '/about': '/images/cap-intro-portrait.jpg',
    '/contact': '/images/cap-hat.jpg'
}

/** Routes that open on a full-bleed photograph, so the bar starts white on it. */
const HERO_ROUTES = new Set(['/', '/portfolio', '/blog', '/about', '/contact'])

export const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const [hovered, setHovered] = useState<string | null>(null)
    const router = useRouter()

    const overHero =
        HERO_ROUTES.has(router.pathname) ||
        router.pathname.startsWith('/work/') ||
        router.pathname.startsWith('/blog/')

    const overlay = useRef<HTMLDivElement>(null)
    const bar = useRef<HTMLElement>(null)
    const preview = useRef<HTMLDivElement>(null)

    // The bar arrives ahead of everything else on the page: the wordmark
    // first, the metadata a beat behind it, both rising 100px. The reference
    // starts these at 0s and 0.1s, before any of the hero imagery moves.
    useIsomorphicLayoutEffect(() => {
        const element = bar.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const brand = element.querySelector<HTMLElement>('[data-bar-brand]')
            const rail = element.querySelectorAll<HTMLElement>('[data-bar-rail]')

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            const timeline = gsap.timeline()

            if (brand) {
                timeline.fromTo(
                    brand,
                    { y: heroLoad.brand.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: heroLoad.brand.duration,
                        ease: heroLoad.brand.ease
                    },
                    heroLoad.brand.delay
                )
            }

            if (rail.length) {
                timeline.fromTo(
                    rail,
                    { y: heroLoad.rail.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: heroLoad.rail.duration,
                        ease: heroLoad.rail.ease,
                        stagger: 0.05
                    },
                    heroLoad.rail.delay
                )
            }
        }, element)

        return () => context.revert()
    }, [])

    // The bar hides as you scroll down and returns the moment you scroll up,
    // so the navigation is never more than a flick away but never in the way.
    useIsomorphicLayoutEffect(() => {
        const element = bar.current

        if (!element) {
            return
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return
        }

        let last = window.scrollY
        let frame = 0

        const onScroll = () => {
            if (frame) {
                return
            }

            frame = window.requestAnimationFrame(() => {
                const current = window.scrollY
                const goingDown = current > last && current > 140

                frame = 0
                last = current

                gsap.to(element, {
                    yPercent: goingDown ? -130 : 0,
                    duration: 0.55,
                    ease: 'power3.out',
                    overwrite: true
                })

                element.dataset.solid = current > 40 ? 'true' : 'false'
            })
        }

        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)

            if (frame) {
                window.cancelAnimationFrame(frame)
            }
        }
    }, [])

    // Open and close choreography: the panel wipes down, the rules draw
    // themselves, then the words rise on a stagger. Closing runs tighter so
    // it feels like a release rather than a second animation.
    useIsomorphicLayoutEffect(() => {
        const element = overlay.current

        if (!element) {
            return
        }

        const words = element.querySelectorAll<HTMLElement>('[data-menu-word]')
        const rules = element.querySelectorAll<HTMLElement>('[data-menu-rule]')
        const meta = element.querySelectorAll<HTMLElement>('[data-menu-meta]')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduced) {
            gsap.set(element, { autoAlpha: open ? 1 : 0 })
            gsap.set([words, meta], { yPercent: 0, opacity: 1 })
            gsap.set(rules, { scaleX: 1 })

            return
        }

        if (open) {
            const timeline = gsap.timeline()

            timeline
                .set(element, { pointerEvents: 'auto' })
                .fromTo(
                    element,
                    { clipPath: 'inset(0% 0% 100% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.85, ease: 'power4.inOut' }
                )
                .fromTo(
                    rules,
                    { scaleX: 0 },
                    { scaleX: 1, duration: 0.8, ease: 'power3.out', stagger: 0.05 },
                    0.3
                )
                .fromTo(
                    words,
                    { yPercent: 115, rotate: 3 },
                    {
                        yPercent: 0,
                        rotate: 0,
                        duration: 0.95,
                        ease: 'expo.out',
                        stagger: 0.07
                    },
                    0.34
                )
                .fromTo(
                    meta,
                    { y: 20, opacity: 0 },
                    { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out', stagger: 0.06 },
                    0.55
                )

            return () => {
                timeline.kill()
            }
        }

        const timeline = gsap.timeline({
            onComplete: () => {
                gsap.set(element, { pointerEvents: 'none' })
            }
        })

        timeline
            .to(words, {
                yPercent: -110,
                duration: 0.5,
                ease: 'power3.in',
                stagger: { each: 0.04, from: 'end' }
            }, 0)
            .to(meta, { opacity: 0, duration: 0.3 }, 0)
            .to(element, {
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: 0.7,
                ease: 'power4.inOut'
            }, 0.24)

        return () => {
            timeline.kill()
        }
    }, [open])

    useEffect(() => {
        if (open) {
            lockScroll()
        } else {
            unlockScroll()
        }

        return () => unlockScroll()
    }, [open])

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    // Desktop only: the hovered link's photograph trails the pointer.
    useEffect(() => {
        const element = preview.current

        if (!element || !open) {
            return
        }

        if (!window.matchMedia('(pointer: fine)').matches) {
            return
        }

        const moveX = gsap.quickTo(element, 'x', { duration: 0.75, ease: 'power3.out' })
        const moveY = gsap.quickTo(element, 'y', { duration: 0.75, ease: 'power3.out' })

        const onMove = (event: PointerEvent) => {
            moveX(event.clientX - window.innerWidth / 2)
            moveY(event.clientY - window.innerHeight / 2)
        }

        window.addEventListener('pointermove', onMove)

        return () => window.removeEventListener('pointermove', onMove)
    }, [open])

    return (
        <>
            <header
                ref={bar}
                data-solid='false'
                data-over={overHero ? 'true' : 'false'}
                data-open={open ? 'true' : 'false'}
                className={cn(
                    'group fixed inset-x-0 top-0 z-[70] transition-colors duration-500',
                    'data-[solid=true]:bg-background/80 data-[solid=true]:backdrop-blur-xl',
                    'text-foreground data-[over=true]:text-white data-[over=true]:data-[solid=true]:text-foreground',
                    // The overlay sits above the bar, so the two z-75 controls
                    // that punch through it always read light while it is open.
                    'data-[open=true]:!text-white data-[open=true]:!bg-transparent data-[open=true]:backdrop-blur-none'
                )}
            >
                {/* Printer's registration marks, exactly as the reference sets them. */}
                <span
                    aria-hidden='true'
                    className='reg-mark left-3 top-3 border-l border-t md:left-5 md:top-5'
                />
                <span
                    aria-hidden='true'
                    className='reg-mark right-3 top-3 border-r border-t md:right-5 md:top-5'
                />

                <div className='edge flex items-center justify-between gap-6 py-4 md:py-5'>
                    {/* Left: the wordmark and a single-line descriptor, joined
                        by a short rule. Everything on one baseline, which is
                        what the old two-line stacks were costing. */}
                    <div className='flex items-center gap-4'>
                        <Link
                            href='/'
                            data-bar-brand
                            className='label z-[75] shrink-0 tracking-[0.12em]'
                            aria-label={`${siteConfig.name}, home`}
                        >
                            {siteConfig.name.toUpperCase()}
                        </Link>

                        {/* The dimming lives on the CHILDREN, never on the
                            [data-bar-rail] node itself: the load tween animates
                            that node's opacity to 1 and an inline value beats
                            any utility class set here. */}
                        <div data-bar-rail className='hidden items-center gap-4 sm:flex'>
                            <span
                                aria-hidden='true'
                                className='block h-px w-6 bg-current opacity-30'
                            />

                            <p className='text-[0.6875rem] leading-none opacity-70'>
                                {author.role}, {CITY}
                            </p>
                        </div>
                    </div>

                    <div className='flex items-center gap-5 md:gap-7'>
                        <div data-bar-rail className='hidden items-center gap-5 md:flex md:gap-7'>
                            <span className='hidden items-center gap-2 text-[0.6875rem] leading-none lg:inline-flex'>
                                {/* bg-current rather than a gold token: the bar
                                    sits on a photograph, on cream, and on a
                                    blurred panel depending on route and scroll,
                                    and current is the only value legible in all
                                    three without picking the wrong lightness. */}
                                <span className='relative flex size-1.5' aria-hidden='true'>
                                    <span className='absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60 motion-reduce:animate-none' />
                                    <span className='relative inline-flex size-1.5 rounded-full bg-current' />
                                </span>

                                {availability}
                            </span>

                            <a
                                href={`mailto:${contact.email}`}
                                className='link-wipe text-[0.6875rem] leading-none'
                            >
                                {contact.email}
                            </a>
                        </div>

                        <button
                            type='button'
                            onClick={() => setOpen((value) => !value)}
                            aria-expanded={open}
                            aria-controls='overlay-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            data-bar-rail
                            className='group/menu z-[75] -my-2 flex shrink-0 items-center gap-2.5 p-2'
                        >
                            <span className='label hidden leading-none sm:block'>
                                {open ? 'Close' : 'Menu'}
                            </span>

                            <span className='relative block h-2.5 w-6' aria-hidden='true'>
                                <span
                                    className={cn(
                                        'absolute left-0 block h-px bg-current transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                        open
                                            ? 'top-1/2 w-full rotate-45'
                                            : 'top-0 w-full group-hover/menu:w-3.5'
                                    )}
                                />
                                <span
                                    className={cn(
                                        'absolute left-0 block h-px w-full bg-current transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                        open ? 'bottom-1/2 -rotate-45' : 'bottom-0'
                                    )}
                                />
                            </span>
                        </button>
                    </div>
                </div>

                {/* Drawn only once the bar has gone solid, so the hero keeps a
                    clean edge. Unmounted while the menu is open rather than
                    fought with a second variant, because two group-data rules
                    on the same property resolve by stylesheet order. */}
                {!open && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-x-0 bottom-0 block h-px origin-left scale-x-0 bg-current opacity-15 transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-data-[solid=true]:scale-x-100'
                    />
                )}
            </header>

            <div
                id='overlay-menu'
                ref={overlay}
                className='pointer-events-none fixed inset-0 z-[72] grain text-[color:var(--cream)]'
                style={{ clipPath: 'inset(0% 0% 100% 0%)', backgroundColor: 'var(--ink)' }}
            >
                <div className='edge flex h-full flex-col justify-between pb-10 pt-28 md:pt-32'>
                    <nav aria-label='Overlay' className='flex-1'>
                        <ul className='flex h-full flex-col justify-center'>
                            {navigation.map((item, index) => (
                                <li key={item.href} className='relative'>
                                    <span
                                        data-menu-rule
                                        className='block h-px w-full origin-left bg-white/15'
                                    />

                                    <Link
                                        href={item.href}
                                        onMouseEnter={() => setHovered(item.href)}
                                        onMouseLeave={() => setHovered(null)}
                                        onFocus={() => setHovered(item.href)}
                                        onBlur={() => setHovered(null)}
                                        className='group/link flex items-baseline gap-5 py-3 md:gap-8 md:py-4'
                                    >
                                        <span className='label w-8 shrink-0 text-white/40'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <span className='line-mask flex-1'>
                                            <span
                                                data-menu-word
                                                className={cn(
                                                    'display-xl inline-block will-change-transform transition-colors duration-500',
                                                    router.pathname === item.href
                                                        ? 'text-accent'
                                                        : 'text-current group-hover/link:text-accent'
                                                )}
                                            >
                                                {item.label}
                                            </span>
                                        </span>

                                        <span className='label hidden shrink-0 text-white/50 opacity-0 transition-opacity duration-500 group-hover/link:opacity-100 md:block'>
                                            View
                                        </span>
                                    </Link>
                                </li>
                            ))}

                            <li aria-hidden='true'>
                                <span data-menu-rule className='block h-px w-full origin-left bg-white/15' />
                            </li>
                        </ul>
                    </nav>

                    <div className='grid gap-8 sm:grid-cols-3'>
                        <div data-menu-meta className='space-y-2'>
                            <p className='label text-white/40'>Studio</p>

                            <p className='text-sm leading-relaxed text-white/75'>
                                {contact.city}
                            </p>
                        </div>

                        <div data-menu-meta className='space-y-2'>
                            <p className='label text-white/40'>Enquiries</p>

                            <a
                                href={`mailto:${contact.email}`}
                                className='link-wipe text-sm text-white/75'
                            >
                                {contact.email}
                            </a>
                        </div>

                        <div data-menu-meta className='space-y-2'>
                            <p className='label text-white/40'>Elsewhere</p>

                            <ul className='flex flex-wrap gap-x-5 gap-y-1'>
                                {social.map((item) => (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            target='_blank'
                                            rel='noreferrer noopener'
                                            className='link-wipe text-sm text-white/75'
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div
                    ref={preview}
                    aria-hidden='true'
                    className='pointer-events-none absolute left-1/2 top-1/2 hidden h-[26rem] w-[19rem] -translate-x-1/2 -translate-y-1/2 lg:block'
                >
                    {navigation.map((item) => (
                        <div
                            key={item.href}
                            className={cn(
                                'absolute inset-0 overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                hovered === item.href
                                    ? 'scale-100 opacity-40'
                                    : 'scale-90 opacity-0'
                            )}
                        >
                            <Image
                                src={previews[item.href] ?? '/images/hero-1.jpg'}
                                alt=''
                                fill
                                sizes='19rem'
                                className='object-cover grayscale'
                            />
                        </div>
                    ))}
                </div>
            </div>
        </>
    )
}
